/* eslint-disable react-hooks/exhaustive-deps */
import {
  formatPageNumber,
  TC_TABLE,
  LIST_TABLE_CONTENTS_LABEL_DISPLAY,
  SIZE_COVER,
  SCALE,
} from '../../constants';
import LegendsOfChartCom from '../common/LegendsOfChartCom';
import PageFooter from '../common/PageFooter';
import RepeatParticipationBlock from '../common/RepeatParticipationBlock';
import CRPreviewSection from '../common/Section';
import TableCom from '../common/TableCom';
import bgCover from '@/assets/image/pdfexport/bg-cover.png';
import DoughnutChart from '@/new-components/CRReportPDF/components/DoughnutChart';
import ChartDataLabels from '@energiency/chartjs-plugin-piechart-outlabels';
import { Box, Grid, Typography, withStyles } from '@material-ui/core';
import { get, isEmpty } from 'lodash';
import React, { useEffect, useMemo } from 'react';

const width = SIZE_COVER.width * SCALE;
const height = SIZE_COVER.height * SCALE;

const CustomContainer = withStyles({
  root: {
    padding: `0 ${80 * SCALE}px`,
    height,
    width,
    margin: '0 auto',
    backgroundImage: `url(${bgCover})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    paddingTop: `${64 * SCALE}px`,
  },
})(Box);
const Title = withStyles({
  root: {
    marginTop: 20 * SCALE,
    fontFamily: 'Lato',
    fontWeight: 700,
    fontSize: 16 * SCALE,
    lineHeight: `${24 * SCALE}px`,
  },
})(Typography);

const Item = withStyles({
  root: {
    paddingLeft: `${16 * SCALE}px`,
    borderRight: '1px solid #CCCCCC',
    fontFamily: 'OpenSans',

    '& .title': {
      color: '#666',
      fontWeight: 600,
      fontSize: 10 * SCALE,
    },

    '& .value': {
      fontFamily: 'Lato',
      marginTop: `${4 * SCALE}px`,
      color: '#333',
      fontWeight: 600,
      fontSize: 18 * SCALE,
      lineHeight: `${24 * SCALE}px`,
    },
    '& .subValue': {
      fontFamily: 'OpenSans',
      marginLeft: `${8 * SCALE}px`,
      color: '#333',
      fontWeight: 600,
      fontSize: 12 * SCALE,
      lineHeight: `${18 * SCALE}px`,
    },

    '&:last-child': {
      borderRight: 'none',
    },
  },
})(Grid);

const HSTotalCholesterol = ({
  corporateData,
  addPaging,
  startPage,
  totalPages,
}) => {
  const tc = get(corporateData, 'healthScreenings.tc.categories');
  const highPercent = tc.find((it) => it.title === 'High').percent;

  const trendings = corporateData?.healthScreenings?.tc?.trendings;

  const textTrending = () => {
    if (trendings?.length >= 2) {
      let latestYears = trendings?.sort((a, b) => b.year - a.year).slice(0, 2);
      const percentLastYear = latestYears[1].percent;
      const percentThisYear = latestYears[0].percent;
      const lastYear = latestYears[1].year;
      if (percentLastYear === percentThisYear) {
        return 'There are increased cases by 0%.';
      } else if (percentThisYear > percentLastYear) {
        return `There are increased cases by ${Math.abs(
          percentThisYear?.formatPercentValue() -
            percentLastYear?.formatPercentValue()
        )?.formatPercentValue()}%.`;
      } else {
        return `GREAT, we have improved by ${Math.abs(
          percentThisYear?.formatPercentValue() -
            percentLastYear?.formatPercentValue()
        )?.formatPercentValue()}% since ${lastYear}!`;
      }
    } else {
      return 'There are increased cases by 0%.';
    }
  };

  const chartData = useMemo(() => {
    return tc?.map((it) => it.percent.formatPercentValue());
  }, [tc]);

  const backgroundColor = [
    'rgba(190, 238, 199, 1)',
    'rgba(249, 225, 161, 1)',
    'rgba(255, 188, 183, 1)',
  ];

  const legends = [
    {
      color: '#BEEEC7',
      title: 'Normal',
    },
    {
      color: '#F9E1A1',
      title: 'Borderline High',
    },
    {
      color: '#FFBCB7',
      title: 'High',
    },
  ];

  const chartPlugins = [ChartDataLabels];

  const dataTable = useMemo(() => {
    if (isEmpty(tc)) return [];
    const table = [...TC_TABLE];

    const dataCutOffs = tc?.map((it, inx) => {
      return {
        color: '#F5F5F5',
        value: `${it.cutOffs}`,
        width: table[0][inx + 1].width,
        height: 24 * SCALE,
      };
    });

    const dataTotal = tc?.map((it, inx) => ({
      color: '#F5F5F5',
      value: `${it.total}`,
      width: table[0][inx + 1]?.width,
      height: 24 * SCALE,
    }));
    const dataPercent = tc?.map((it, inx) => ({
      color: '#F5F5F5',
      value: `${it.percent.formatPercentValue()}%`,
      width: table[0][inx + 1].width,
      height: 24 * SCALE,
    }));

    let header = [];
    header.push(table[0][0]);
    header = header.concat(
      tc?.map((it, inx) => ({
        color: '#E0E0E0',
        value: it.title,
        width: table[0][inx + 1]?.width,
        height: table[0][inx + 1]?.height,
      }))
    );

    table[0] = table[0].map((it, inx) => ({
      ...header[inx],
    }));

    table[1] = table[1].concat(dataCutOffs);
    table[2] = table[2].concat(dataTotal);
    table[3] = table[3].concat(dataPercent);

    return table;
  }, [tc]);

  const renderDatas = useMemo(() => {
    let datas = trendings
      ?.sort((a, b) => a.year - b.year)
      .map((it) => ({
        value: `${it.percent?.formatPercentValue()}%`,
        subValue: ` (${it.total} pax)`,
        title: `${it.year}`,
      }));
    return datas;
  }, [trendings]);

  useEffect(() => {
    addPaging(LIST_TABLE_CONTENTS_LABEL_DISPLAY.HealthScreeningFindings);
  }, []);

  return (
    <>
      <CustomContainer>
        <Box display="flex" flexDirection="column" minHeight="100%">
          <Box>
            <CRPreviewSection title="Total Cholesterol (TC)" />
          </Box>

          <Box
            display="flex"
            alignItems="center"
            gridGap={40 * SCALE}
            mt={2 * SCALE}
          >
            <Box width={`${200 * SCALE}px`} height={`${200 * SCALE}px`}>
              <DoughnutChart
                chartData={chartData}
                chartPlugins={chartPlugins}
                backgroundColor={backgroundColor}
                animationDuration={400}
                isLabelOutside
              />
            </Box>
            <Box width={`${200 * SCALE}px`}>
              <LegendsOfChartCom data={legends} />
            </Box>
          </Box>

          <Box
            fontFamily="OpenSans"
            mt={2 * SCALE}
            fontSize={12 * SCALE}
            lineHeight={`${18 * SCALE}px`}
          >
            {highPercent.formatPercentValue()}% has High TC.
          </Box>

          <Box mt={2 * SCALE}>
            <TableCom data={dataTable} />
          </Box>
          {corporateData?.healthScreenings?.tc?.trendings && (
            <>
              <Title>Trending: High TC Readings</Title>
              <Box
                fontFamily="OpenSans"
                mt={2 * SCALE}
                fontSize={12 * SCALE}
                lineHeight={`${18 * SCALE}px`}
              >
                {textTrending()}
              </Box>
              <Box
                mt={2 * SCALE}
                display="flex"
                bgcolor="#E2F2FF"
                width="100%"
                height={64 * SCALE}
                borderRadius={10 * SCALE}
                py={1.5 * SCALE}
              >
                <Grid container>
                  {renderDatas?.map((it, inx) => (
                    <Item
                      item
                      xs={renderDatas?.length >= 3 ? 4 : 6}
                      key={`${it.title}-${inx}`}
                    >
                      <div className="title">{it.title}</div>
                      <div className="value">
                        {it.value}

                        <span className="subValue">{it.subValue}</span>
                      </div>
                    </Item>
                  ))}
                </Grid>
              </Box>
              {corporateData?.healthScreenings?.tc
                ?.repeatParticipantTrendings != null && (
                <RepeatParticipationBlock
                  data={corporateData?.healthScreenings?.tc}
                  style={{ marginTop: 16 * SCALE }}
                />
              )}
            </>
          )}
          <PageFooter
            page={formatPageNumber(startPage)}
            totalPages={totalPages}
            title={LIST_TABLE_CONTENTS_LABEL_DISPLAY.HealthScreeningFindings}
          />
        </Box>
      </CustomContainer>
    </>
  );
};

export default HSTotalCholesterol;
